// Used to switch a browser from batched analytics back to `ahoy.track`, one event to a request, once batching
// shows a fault. The switch is remembered in `localStorage` for `duration`, after which the browser tries batching
// again, so batching resumes by itself once a later deploy fixes the fault. The page that trips the switch also keeps
// the deadline in `trippedUntil`, so a browser that cannot store the switch still falls back on that page for
// `duration`. Both copies of the deadline expire together, so a page left open past it resumes batching.
//
// A trip is passed to `report` from a `reportRate` sample of browsers, and only when no earlier page's trip is still
// active, so a fault every visitor hits is reported a number of times proportional to its reach, not once per visitor.
//
// Decision record: https://github.com/combinaut/sparkle/wiki/ADR-0029-Batch-Analytics-Events-in-Our-Own-Modules-Around-a-Vendored-Ahoy-JS
export class AnalyticsEventFallback {
  // @param report {Function} called with the reason for a sampled trip
  // @param options {Object}
  //   - `duration`: the milliseconds a trip lasts
  //   - `reportRate`: the share of trips reported
  //   - `key`: the `localStorage` key the trip is remembered under
  constructor(report, options) {
    options = options || {};

    this.key = options.key || "sparkle_analytics_fallback_until";
    this.report = report;
    this.duration = options.duration || 24 * 60 * 60 * 1000;
    this.reportRate = options.reportRate === undefined ? 0.05 : options.reportRate;
    this.trippedUntil = 0;
    this.listeners = [];
  }

  active() {
    if (this.trippedUntil > Date.now()) {
      return true;
    }

    try {
      return Number(localStorage.getItem(this.key)) > Date.now();
    } catch {
      return false;
    }
  }

  // Calls `listener` once, when this page trips the fallback.
  onTrip(listener) {
    this.listeners.push(listener);
  }

  // Switches this browser to `ahoy.track` for `duration`. A trip while this page's own trip is in effect does nothing.
  trip(reason) {
    if (this.trippedUntil > Date.now()) {
      return;
    }

    const alreadyActive = this.active();

    this.trippedUntil = Date.now() + this.duration;

    try {
      localStorage.setItem(this.key, String(Date.now() + this.duration));
    } catch {
      // The fallback lasts for this page only.
    }

    if (!alreadyActive && Math.random() < this.reportRate) {
      this.report(reason);
    }

    this.listeners.forEach((listener) => listener(reason));
  }
}
